import { CONFIG } from '../config';
import { versionString } from './source';
import type { AttractSlide, CloudinaryAsset, FileRef, GalleryImage, Manifest, ProjectEntry, RawContent } from './types';
import { hashKey, limitSize, variantUrl, type VariantName } from './variants';

export interface BuildDeps {
  /** Descarga una LQIP y la devuelve como data URL base64 */
  fetchLqip: (url: string) => Promise<string>;
  /** Manifiesto activo: sus LQIP se reutilizan sin volver a descargarlas */
  previous?: Manifest | null;
  concurrency?: number;
}

const ESTADOS = new Set(['enPlanos', 'enConstruccion', 'entregaInmediata']);

/**
 * Construye el manifiesto completo a partir de la respuesta de Sanity: proyectos, URLs ya
 * transformadas, claves de contenido y LQIP en base64. No descarga imágenes grandes.
 */
export async function buildManifest(raw: RawContent, deps: BuildDeps): Promise<Manifest> {
  const files = new Map<string, FileRef>();
  const lqipJobs = new Map<string, string>(); // url -> dataUrl (rellenado al final)

  const ref = (a: CloudinaryAsset, v: VariantName): FileRef => {
    const url = variantUrl(a, v);
    const key = hashKey(url);
    const f = { key, url };
    files.set(key, f);
    return f;
  };
  const lqipOf = (a: CloudinaryAsset): string => {
    const url = variantUrl(a, 'lqip');
    lqipJobs.set(url, '');
    return url; // se reemplaza por el data URL más abajo
  };
  const valid = (a: CloudinaryAsset | null | undefined): a is CloudinaryAsset => !!a && typeof a.public_id === 'string' && a.public_id.length > 0;

  const gallery = (list: CloudinaryAsset[] | null | undefined, pid: string, kind: string): GalleryImage[] =>
    (list || []).filter(valid).map((a, i) => {
      const full = limitSize(a, 'full');
      const caption = a.context?.custom?.caption || a.context?.custom?.alt || undefined;
      return {
        id: `${pid}:${kind}:${a._key || i}`,
        caption,
        fullW: full.w,
        fullH: full.h,
        lqip: lqipOf(a),
        thumb: ref(a, 'thumb'),
        main: ref(a, 'main'),
        full: ref(a, 'full'),
      };
    });

  const projects: ProjectEntry[] = (raw.projects || [])
    .filter((p) => valid(p.portada) && ((p.renders || []).some(valid) || (p.fotosReales || []).some(valid)))
    .map((p) => {
      const portada = p.portada as CloudinaryAsset;
      const logo = valid(p.logo) ? p.logo : null;
      /* Casa modelo por tipos. Mientras el proyecto no tenga tipos, los renders sueltos
         de "Casa modelo (sin tipo)" se muestran como un único modelo. */
      const tipos = (p.tiposCasa || [])
        .map((t, k) => ({
          id: `${p._id}:t:${t._key || k}`,
          nombre: (t.nombre || '').trim() || `Modelo ${k + 1}`,
          detalle: (t.detalle || '').trim(),
          imagenes: gallery(t.imagenes, p._id, `t${t._key || k}`),
        }))
        .filter((t) => t.imagenes.length > 0);
      const sueltas = gallery(p.casasModelo, p._id, 'c');
      const modelos = tipos.length ? tipos : sueltas.length ? [{ id: `${p._id}:t:0`, nombre: 'Casa modelo', detalle: '', imagenes: sueltas }] : [];
      return {
        id: p._id,
        slug: p.slug || p._id,
        nombre: p.nombre || 'Proyecto',
        ubicacion: p.ubicacion || '',
        estado: p.estado && ESTADOS.has(p.estado) ? p.estado : null,
        cover: { lqip: lqipOf(portada), card: ref(portada, 'card'), attract: ref(portada, 'attract') },
        logo: logo ? { file: ref(logo, 'logo'), ratio: (logo.width || 4) / (logo.height || 1) } : null,
        renders: gallery(p.renders, p._id, 'r'),
        fotos: gallery(p.fotosReales, p._id, 'f'),
        casas: modelos.flatMap((m) => m.imagenes),
        modelos,
        mapa: gallery(p.mapa, p._id, 'm'),
      };
    });

  const custom = (raw.settings?.imagenesReposo || []).filter(valid);
  const reposo: AttractSlide[] = custom.length
    ? custom.map((a) => ({ lqip: lqipOf(a), file: ref(a, 'attract'), projectId: null }))
    : projects.map((p) => ({ lqip: p.cover.lqip, file: p.cover.attract, projectId: p.id }));

  // LQIP: reutiliza las del manifiesto anterior; descarga solo las nuevas (pequeñas, ~300 B)
  const prev = deps.previous?.lqipIndex || {};
  const pending = [...lqipJobs.keys()].filter((u) => {
    if (prev[u]) lqipJobs.set(u, prev[u]);
    return !prev[u];
  });
  await pool(pending, deps.concurrency ?? 4, async (u) => {
    lqipJobs.set(u, await deps.fetchLqip(u));
  });
  const lq = (u: string) => lqipJobs.get(u) || '';
  for (const p of projects) {
    p.cover.lqip = lq(p.cover.lqip);
    for (const g of [...p.renders, ...p.fotos, ...(p.casas || []), ...(p.mapa || [])]) g.lqip = lq(g.lqip);
  }
  for (const s of reposo) s.lqip = lq(s.lqip);

  const secs = Number(raw.settings?.segundosInactividad);
  return {
    schema: 1,
    version: versionString(raw.version),
    builtAt: new Date().toISOString(),
    settings: { segundosInactividad: Number.isFinite(secs) && secs >= 15 ? secs : CONFIG.defaultIdleSeconds },
    reposo,
    projects,
    files: [...files.values()],
    lqipIndex: Object.fromEntries(lqipJobs),
  };
}

export async function pool<T>(items: T[], n: number, fn: (item: T, i: number) => Promise<void>): Promise<void> {
  let next = 0;
  let failed: unknown = null;
  const worker = async () => {
    while (next < items.length && !failed) {
      const i = next++;
      try {
        await fn(items[i], i);
      } catch (e) {
        failed = failed || e;
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(n, items.length) }, worker));
  if (failed) throw failed;
}
